import './Logo.css'
import { useEffect, useState } from 'react'

/* Put your two logo files in /public:
     logo-on-light.png  (white-background logo)
     logo-on-dark.png   (black-background logo)
   This component removes the background of the picture (white for the light logo, black for the
   dark one) so the logo sits directly on the page with no box, and trims the empty space around it.
   Transparent PNGs also work: they are only trimmed.
   If the image can't load, it shows the text "iPOINT" instead. */

const cache = {}

function prepare(src, onDark) {
  if (cache[src]) return Promise.resolve(cache[src])
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onerror = reject
    img.onload = () => {
      const w = img.naturalWidth, h = img.naturalHeight
      const c = document.createElement('canvas')
      c.width = w; c.height = h
      const ctx = c.getContext('2d', { willReadFrequently: true })
      ctx.drawImage(img, 0, 0)
      const data = ctx.getImageData(0, 0, w, h)
      const d = data.data

      // background -> transparent. The colour is "un-mixed" from the background, so the soft edges
      // of the letters stay smooth and off-white / noisy backgrounds disappear completely.
      const alreadyClear = d[3] < 250 // top-left pixel is transparent: nothing to remove
      if (!alreadyClear) {
        for (let i = 0; i < d.length; i += 4) {
          const r = d[i], g = d[i + 1], b = d[i + 2]
          const a0 = onDark ? Math.max(r, g, b) / 255 : 1 - Math.min(r, g, b) / 255 // how much "ink" this pixel has
          if (a0 < 0.004) { d[i] = d[i + 1] = d[i + 2] = 0; d[i + 3] = 0; continue }
          const k = onDark ? 0 : 255 * (1 - a0)
          d[i] = Math.max(0, Math.min(255, (r - k) / a0))
          d[i + 1] = Math.max(0, Math.min(255, (g - k) / a0))
          d[i + 2] = Math.max(0, Math.min(255, (b - k) / a0))
          d[i + 3] = Math.round(Math.max(0, Math.min(1, (a0 - 0.07) / 0.55)) * 255) // ignore faint noise
        }
        ctx.putImageData(data, 0, 0)
      }

      // trim the empty space around the wordmark
      let x0 = w, y0 = h, x1 = 0, y1 = 0
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          if (d[(y * w + x) * 4 + 3] > 40) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y }
        }
      }
      if (x1 <= x0 || y1 <= y0) { x0 = 0; y0 = 0; x1 = w - 1; y1 = h - 1 } // nothing found: use whole image
      const pad = 4
      x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad)
      x1 = Math.min(w - 1, x1 + pad); y1 = Math.min(h - 1, y1 + pad)
      const bw = x1 - x0 + 1, bh = y1 - y0 + 1
      const out = document.createElement('canvas')
      out.width = bw; out.height = bh
      out.getContext('2d').drawImage(c, x0, y0, bw, bh, 0, 0, bw, bh)
      resolve(cache[src] = { url: out.toDataURL('image/png'), w: bw, h: bh })
    }
    img.src = src
  })
}

export default function Logo({ onDark = false, width = 190 }) {
  const src = `${process.env.PUBLIC_URL}/${onDark ? 'logo-on-dark.png' : 'logo-on-light.png'}`
  const [box, setBox] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    setFailed(false)
    prepare(src, onDark).then(b => live && setBox(b)).catch(() => live && setFailed(true))
    return () => { live = false }
  }, [src, onDark])

  if (failed) return <span className={`logo-text ${onDark ? 'logo-text--dark' : ''}`}>iPOINT</span>
  if (!box) return <span className="logo" style={{ width, height: width * 0.3 }} />

  return (
    <span className={`logo ${onDark ? 'logo--dark' : ''}`} style={{ width, height: (box.h * width) / box.w }}
      role="img" aria-label="iPOINT Premium Accessories & Repair">
      <img src={box.url} alt="" draggable="false" />
    </span>
  )
}